import { useState, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { pathConverter, iframePaths, shouldNavigate, checkIframeOrigin } from '../utils';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { DISABLE_IFRAME_BG_LOADING } from 'config';

type IframeProps = {
  path: string;
};

export const Iframe = ({ path }: IframeProps) => {
  const [iframePath, setIframePath] = useState('');
  const [loading, setLoading] = useState(false);
  const hidden =
    !iframePaths.includes(path) && !iframePaths.some(p => path.toLowerCase().includes(p));
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      (iframePaths.includes(path) && iframePath !== path) ||
      iframePaths.some(p => path.toLowerCase().includes(p))
    ) {
      if (!iframePath) {
        setLoading(true);
      }
      setIframePath(path);
    }
  }, [path]);

  useEffect(() => {
    if (!DISABLE_IFRAME_BG_LOADING) {
      setTimeout(() => {
        setIframePath('/style');
      }, 1000);
    }
  }, []);

  useEffect(() => {
    if (wrapperRef.current) {
      const debouncedHandleResize = debounce(entries => {
        for (const entry of entries) {
          setDimensions({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      }, 200);
      const observer = new ResizeObserver(debouncedHandleResize);
      observer.observe(wrapperRef.current);
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const allowed = checkIframeOrigin(event.origin);
      if (!allowed) {
        return;
      }
      if (event.data.type === 'relocateTo' && !hidden) {
        const newUrl = shouldNavigate(event.data.payload, location.pathname);
        if (newUrl) {
          navigate(newUrl);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [location, hidden]);

  return (
    <IframeWrapper ref={wrapperRef} hidden={hidden || !iframePath}>
      {iframePath ? (
        <>
          <HidableIframe>
            <IframeEl
              title={path}
              id="iframe-old-spa"
              width={dimensions.width}
              height={dimensions.height}
              src={pathConverter(iframePath)}
              onLoad={() => {
                setLoading(false);
              }}
            />
          </HidableIframe>
          {loading ? <Loader>Loading iframe..</Loader> : null}
        </>
      ) : null}
    </IframeWrapper>
  );
};

const IframeEl = styled.iframe`
  border: none;
`;

const IframeWrapper = styled.div`
  position: relative;
  display: flex;
  height: ${p => (p.hidden ? '0' : '100vh')};
  width: 100%;
`;

const HidableIframe = styled.div`
  position: absolute;
  inset: 0;
`;

const Loader = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  width: 200px;
  height: 50px;
  top: 30px;
  border-radius: 5px;
  border: 1px solid #ccc;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
`;
